import { Currency } from 'api/Admin/Currency';
import { Timezone } from 'api/Admin/Timezones';
import { KoddiListFilterParams, Pagination, ReportFilter } from 'app/types';
import { Channel, KoddiAPIResponse } from 'types';
import { AdGroupBidder } from '../AdGroups';

export type AdvertiserStatus = 'active' | 'inactive';

type AdvertiserGroup = {
    id: number;
    name: string;
};
type MemberGroup = {
    id: number;
    name: string;
};

export type AdvertiserWireTransferInvoicesReport = {
    advertiser_payment_provider_transaction_id: number;
    amount: string;
    end_date: string;
    start_date: string;
    status: 'Paid' | 'Unpaid' | 'Overdue';
    receipt_link: string;
};

export type WireTransferResponse = {
    unpaginated_count: number;
    total_amount_unpaginated: number;
    total_amount: number;
    invoices: AdvertiserWireTransferInvoicesReport[] | null;
};

export type WireTransferRequestBody = {
    pagination: Pagination;
    sort: string[] | undefined;
    filters: ReportFilter[];
};

export type WireTransferParams = {
    userHasAdminAccess: boolean;
    isWireTransfersEnabled: boolean;
    body: WireTransferRequestBody;
};

export type AdvertiserBillingAddress = {
    email: string;
    address: string;
    invoice_method_id: number;
    address_line_2?: string;
    country: string;
    name: string;
    city: string;
    state: string;
    zip: string;
    stripe_customer_id: string;
    tax_type?: string | null;
    tax_id?: string | null;
};

export type Advertiser = {
    advertiser_id: number;
    advertiser_admins: [];
    active_user_count: number;
    advertiser_group: AdvertiserGroup;
    member_id: number;
    member_group_id: number;
    name: string;
    member_group: MemberGroup;
    entity_count: number;
    description: string;
    ad_category_id?: number;
    ad_category?: {
        id: number;
        name: string;
        parent_id: string;
        child_id: string;
    };
    agency_name: string;
    timezone: Timezone;
    currency: Currency;
    currency_id?: number;
    contact_name: string;
    contact_address1: string;
    contact_address2: string;
    contact_city: string;
    contact_country: string;
    contact_state: string;
    contact_zip: string;
    contact_phone: string;
    contact_email: string;
    deleted: boolean;
    status: AdvertiserStatus;
    created_at: string;
    last_modified: string;
    last_edited_by: string;
    timezone_id: number;
    channels?: Channel[];
    total_pre_pay_balance?: number;
    billing: AdvertiserBillingAddress;
    experience_ids: number[];
    entity_group_ids: number[];
    entities_filter?: ReportFilter[] | null;
    additional_entities_filter?: string[];
    has_setup_stripe_payment?: boolean;
    is_on_financial_hold?: boolean;
    goal_amount?: number;
};

export type CreateAdvertiserPayload = Partial<Advertiser>;
export type UpdateAdvertiserPayload = Partial<Advertiser>;

export type ReportAdvertiser = {
    active_users_count: number;
    advertiser_group: { id: number; name: string };
    currency: Currency;
    entity_count: number;
    id: number;
    last_modified: string;
    member_group: { id: number; name: string };
    name: string;
    status: string;
    timezone: number; // @TODO god damnit not again
    is_on_financial_hold: boolean;
};
export type ReportAdvertiserList = ReportAdvertiser[];
export type ReportAdvertiserResponse = {
    advertisers: ReportAdvertiserList;
};

export type AdvertiserGetListResponse = KoddiAPIResponse<{
    advertisers: Advertiser[];
}>;

export type AdvertiserEntity = {
    address: string;
    country: string;
    id: string;
    market: string;
    name: string;
    postal_code: string;
    koddi_entity_id: number;
};

export type AdvertiserBalanceByFundingType = {
    amount: number;
    type: {
        id: number;
        name: string;
    };
};

export type AdvertiserInvoicesReport = {
    advertiser_payment_provider_transaction_id: number;
    amount: string;
    end_date: string;
    start_date: string;
    status: string;
    receipt_link: string;
};

export type PCLSum = { [key: string]: number };

export type AdvertiserPrePayBalanceReport = {
    advertiser_admins?: [];
    total_pre_pay_balance: number;
};

export type AdvertiserTransactionHistory = {
    amount: number | string;
    balance_event_id: number;
    balance_event_type: { id: number; name: string };
    category: string;
    expiration_date: string;
    funding_type: { id: number; name: string };
    payment_provider_transaction_id: string | null;
    receipt_link: string | null;
    status: string | null;
    transaction_date: string;
    transaction_id: number;
};

export type PaymentMethod = {
    payment_method_id: string;
    type: any;
    name: string;
    billing_address: any;
    data: {
        brand: string;
        checks: any;
        country: string;
        description: string;
        exp_month: number;
        exp_year: number;
        fingerprint: string;
        funding: string;
        iin: string;
        issuer: string;
        last4: string;
        networks: any;
        three_d_secure_usage: {
            supported: boolean;
        };
        wallet: any;
    };
};

export type StripeBillingAddress = {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    postal_code: string | null;
    tax_type?: string | null;
    tax_id?: string | null;
};

export type SupportedPaymentTypes = 'card' | 'link' | 'us_bank_account';

export type UpdateAdvertiserPaymentPayload = {
    name?: string;
    email?: string;
    billing_address?: StripeBillingAddress;
    type?: SupportedPaymentTypes;
    data: {
        exp_month?: string | number; // for card
        exp_year?: string | number; // for card
        account_holder_type?: string; // for us_bank_account (ACH)
    };
};

export type AutoBillSettings = {
    auto_rebill_id: number;
    advertiser_id: number;
    payment_provider: {
        id: number;
        name: string;
    };
    payment_provider_payment_method_id: string;
    billing_cycle: {
        id: number;
        name?: 'Daily' | 'Weekly' | 'Monthly';
    };
    start_date: string;
    next_payment_date: string;
    amount: number;
};

export type TaxTypeOptions = {
    name: string;
    label: string;
    locale: string;
    example: string;
};

export type AdvertiserBillingEvent = {
    invoice_id: string;
    payment_date: string;
    item: {
        name: string;
        amount: number;
        currency: {
            id: number;
            name: string;
            code: string;
            symbol: string;
            digits: string;
        };
    };
    bill_to: {
        name: string;
        email: string;
        address: string;
        address_line_2: string | null;
        city: string;
        country: string;
        state: string | null;
        zip: string | null;
        tax_type: string | null;
        tax_id: string | null;
    };
};

export type AdvertiserMetaDataKey = {
    advertiser_key_name: string;
    label: string;
};

export type PCLPayload = {
    memberGroupId: number;
    advertiserId: number;
    allSelected?: boolean;
    startDate?: string;
    endDate?: string;
    bidders?: AdGroupBidder[] | null;
    filters?: ReportFilter[];
    budgetType?: 'Daily' | 'Monthly' | 'Weekly' | 'Custom';
};

export type AdvertiserSpendLimit = {
    advertiser_spend_limit_id: number;
    amount: number;
};

export type AdvertiserSpendLimitWithID = AdvertiserSpendLimit & {
    advertiser_id: number;
};

export type AdvertiserSpendLimitsWithID = AdvertiserSpendLimitWithID[];

export type AdvertiserSpendLimits = {
    advertiser_id: number;
    amount: number;
};

export type CreateAdvertiserLimitsPayload = {
    advertiser_spend_limits: AdvertiserSpendLimits[];
};

export type AdvertiserSpendLimitsWithId = AdvertiserSpendLimits & {
    advertiser_spend_limit_id: number;
};

export type CreateAdvertiserLimitsResponse = {
    advertiser_spend_limits: AdvertiserSpendLimitsWithId[];
};

export type CreateAdvertiserAutoBillSettingsType = {
    payment_provider: {
        id: number;
    };
    payment_provider_payment_method_id: string;
    billing_cycle: {
        id: number;
    };
    start_date: string;
    amount: number;
};

export type CreateDSPAssociationPayload = {
    dsp_seat_id: string;
    floodlight_id: number;
    floodlight_activity_id: number;
    integration_type: string;
};

export type EntitiesReportParams = {
    advertiserId: number;
    params: KoddiListFilterParams & {
        filters?: ReportFilter[];
    };
};

export type EntitiesReportMutationParams = {
    advertiserId: number;
    filters?: ReportFilter[];
    pagination?: {
        count: number;
        start: number;
    };
};

export type EntitiesReportResponse = {
    entities: AdvertiserEntity[];
    unpaginated_count: number;
};

export enum BalanceEventTypeId {
    FundingAuto = 1,
    FundingUIForm = 2,
    FundingAPI = 3,
    SystemDebitUIForm = 4,
    SystemDebitAPI = 5,
    Expiration = 6,
    Click = 7,
    Invoice = 8,
    BalanceRefill = 9,
    ServiceFee = 10,
    Surcharge = 11,
    TrueUp = 12,
}

export type ApplySystemCreditPayload = {
    expiration_date?: string;
    funding_notes?: string;
    ticket_id?: string;
    balance_event_type_id: BalanceEventTypeId;
    koddi_entity_id?: number;
    request_time?: string;
    currency: {
        id: number;
    };
    transactions: {
        amount: number;
        funding_type_id: number;
        tactic_channel_id?: number;
        publisher_id?: number;
    }[];
};

export type BudgetForecastParams = {
    budget: number;
    memberGroupId: number;
    advertiserId: number;
    adGroupId: number;
};

export type BudgetForecastResponse = {
    member_group_id: number;
    ad_group_id: number;
    new_budget: number;
    projected_impressions: number;
    projected_clicks: number;
    projected_conversions: number;
    estimated_time_live: number;
    warning: string;
};

export type AttributeValues = string[];

export type AttributeValuesResponse = {
    attribute_key_values: AttributeValues;
};
